/**
 * Influencer API Service — FastAPI backend
 * Base URL: https://bc-api.influencer.in  (BC_API_HOST)
 * Base path: /api/v1/campaigns/
 *
 * This is a separate service from the main API_HOST.
 * Override with REACT_APP_BC_API_HOST env variable if needed.
 */

import { BC_API_HOST } from '../config';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('access')}`,
});

const parseResponseBody = async (response) => {
  const text = await response.text();

  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    // Some backend paths may return plain-text or HTML error pages.
    return { message: text };
  }
};

const buildFallbackError = (response, parsedBody) => {
  if (parsedBody && typeof parsedBody === 'object') return parsedBody;
  return { message: `Request failed with status ${response.status}` };
};

const handleResponse = async (response) => {
  const parsedBody = await parseResponseBody(response);

  if ([200, 201, 204].includes(response.status)) {
    return parsedBody;
  }

  return Promise.reject(buildFallbackError(response, parsedBody));
};

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

/**
 * List available campaign objectives.
 * GET /api/v1/objectives/
 */
export function listObjectives() {
  return fetch(`${BC_API_HOST}/api/v1/objectives/`, {
    method: 'GET',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * List all campaigns for the authenticated user's brand.
 * GET /api/v1/campaigns/
 */
export function listCampaigns() {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/`, {
    method: 'GET',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * Create a campaign — with or without BrandConnect.
 * POST /api/v1/campaigns/
 *
 * Payload (without BC):
 *   { title, start_date, end_date, objective_id, platforms: [{platform, post_type, count}] }
 *
 * Additional BC fields:
 *   { bc_tracking_enabled, brand_channel_handle, brand_channel_id, bc_brief, bc_environment }
 */
export function createInfluencerCampaign(payload) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  }).then(handleResponse);
}

/**
 * Get detailed campaign info with aggregated engagement stats.
 * GET /api/v1/campaigns/:campaignId
 */
export function getCampaignDetail(campaignId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}`, {
    method: 'GET',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * Partially update campaign fields.
 * PATCH /api/v1/campaigns/:campaignId
 */
export function updateCampaign(campaignId, payload) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  }).then(handleResponse);
}

/**
 * Archive (soft-delete) a campaign.
 * DELETE /api/v1/campaigns/:campaignId/archive
 */
export function archiveCampaign(campaignId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/archive`, {
    method: 'DELETE',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * Mark campaign as complete.
 * POST /api/v1/campaigns/:campaignId/complete
 */
export function completeCampaign(campaignId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/complete`, {
    method: 'POST',
    headers: getHeaders(),
  }).then(handleResponse);
}

// ---------------------------------------------------------------------------
// Shortlisted Creators
// ---------------------------------------------------------------------------

/**
 * List all shortlisted creators for a campaign (with BC deal info).
 * GET /api/v1/campaigns/:campaignId/creators
 */
export function listShortlistedCreators(campaignId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/creators`, {
    method: 'GET',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * Shortlist a creator for a campaign.
 * If campaign has BC enabled, a BC deal is created automatically under the hood.
 * POST /api/v1/campaigns/:campaignId/creators
 *
 * Payload:
 *   {
 *     influencer_id,
 *     creator_channel_id,
 *     final_price,
 *     expected_video_count,
 *     creator_cost_units,
 *     creator_cost_currency,
 *     partnership_terms,
 *     deal_link: { uri, label }   // optional, BC only
 *   }
 */
export function shortlistCreator(campaignId, payload) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/creators`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  }).then(handleResponse);
}

// ---------------------------------------------------------------------------
// Videos
// ---------------------------------------------------------------------------

/**
 * List all videos across all deals in a campaign (organic + paid stats).
 * GET /api/v1/campaigns/:campaignId/videos
 */
export function listCampaignVideos(campaignId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/videos`, {
    method: 'GET',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * Link a YouTube video to a shortlisted creator.
 * If the creator has a BC deal, a video collaboration is auto-created.
 * POST /api/v1/campaigns/:campaignId/creators/:shortlistedId/videos
 *
 * Payload: { youtube_video_id }
 */
export function linkVideoToCreator(campaignId, shortlistedId, youtubeVideoId) {
  return fetch(
    `${BC_API_HOST}/api/v1/campaigns/${campaignId}/creators/${shortlistedId}/videos`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ youtube_video_id: youtubeVideoId }),
    },
  ).then(handleResponse);
}

/**
 * Unlink a video from campaign and BrandConnect collaboration.
 * DELETE /api/v1/campaigns/:campaignId/videos/:youtubeVideoId
 */
export function unlinkCampaignVideo(campaignId, youtubeVideoId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/videos/${youtubeVideoId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * Sync a video's BrandConnect data from YouTube.
 * POST /api/v1/videos/:youtubeVideoId/bc/sync
 */
export function syncVideo(youtubeVideoId) {
  return fetch(`${BC_API_HOST}/api/v1/videos/${youtubeVideoId}/bc/sync`, {
    method: 'POST',
    headers: getHeaders(),
  }).then(handleResponse);
}

// ---------------------------------------------------------------------------
// Insights & Reports
// ---------------------------------------------------------------------------

/**
 * Update BC paid metrics on a YouTube video from BrandConnect insights.
 * All fields except yt_video_id are optional.
 * PUT /api/v1/campaigns/:campaignId/videos/insights
 *
 * Payload:
 *   {
 *     yt_video_id,
 *     paid_view_count, paid_like_count, paid_comment_count, paid_share_count,
 *     paid_engagement_rate, paid_watch_time, paid_avg_view_duration,
 *     engaged_views, paid_engaged_views, playlist_adds, paid_playlist_adds,
 *     avg_watch_pct, has_product_placement, brand_mentions
 *   }
 */
export function updateVideoInsights(campaignId, payload) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/videos/insights`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  }).then(handleResponse);
}

/**
 * Get aggregated organic/paid video report for a campaign.
 * GET /api/v1/campaigns/:campaignId/report
 */
export function getCampaignReport(campaignId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/report`, {
    method: 'GET',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * Cancel a creator deal for a campaign.
 * POST /api/v1/campaigns/:campaignId/creators/:shortlistedId/cancel-deal
 */
export function cancelCreatorDeal(campaignId, shortlistedId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/creators/${shortlistedId}/cancel-deal`, {
    method: 'POST',
    headers: getHeaders(),
  }).then(handleResponse);
}

/**
 * Activate (or re-activate) a deal under a campaign.
 * POST /api/v1/campaigns/:campaignId/deals/:dealId/activate
 *
 * Note: backend may treat this as "reactivate" if deal is completed.
 */
export function activateDeal(campaignId, dealId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/deals/${dealId}/activate`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({}),
  }).then(handleResponse);
}

/**
 * Mark a deal as complete under a campaign.
 * POST /api/v1/campaigns/:campaignId/deals/:dealId/complete
 */
export function completeDeal(campaignId, dealId) {
  return fetch(`${BC_API_HOST}/api/v1/campaigns/${campaignId}/deals/${dealId}/complete`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({}),
  }).then(handleResponse);
}


export function validateBrandChannel(payload) {
  return fetch(`${BC_API_HOST}/api/v1/channels/validate-brand-channel`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  }).then(handleResponse);
}
